import React, { useMemo } from 'react';
import { Switch, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { ticketColors } from '../../shared/theme/ticketColors';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import { CatalogProductFormSegmentedRow } from './CatalogProductFormSegmentedRow';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';
import type { ProductDraft } from '../domain/productDraft';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'draft'
  | 'onBookingModeChange'
  | 'onBookingPaymentModeChange'
  | 'onBookingDepositAmountChange'
  | 'onBookingDepositPercentChange'
  | 'onEvaluationProductIdChange'
  | 'onDurationMinutesChange'
  | 'onBufferBeforeMinutesChange'
  | 'onBufferAfterMinutesChange'
  | 'serviceProductOptions'
  | 'serviceProductOptionsStatus'
  | 'onRetryServiceProductOptions'
  | 'mobile'
  | 'desktopRail'
> & {
  onlineStoreEnabled: boolean;
};

const BOOKING_MODE_SEGMENTS = [
  { value: 'direct', label: 'Directo' },
  { value: 'quote_required', label: 'Cotización' },
] as const;

const PAYMENT_MODE_SEGMENTS = [
  { value: 'inherit', label: 'Como la tienda' },
  { value: 'none', label: 'Sin cobro' },
  { value: 'deposit', label: 'Seña' },
  { value: 'full', label: 'Total' },
] as const;

export function CatalogProductFormBookingFields({
  draft,
  onBookingModeChange,
  onBookingPaymentModeChange,
  onBookingDepositAmountChange,
  onBookingDepositPercentChange,
  onEvaluationProductIdChange,
  onDurationMinutesChange,
  onBufferBeforeMinutesChange,
  onBufferAfterMinutesChange,
  serviceProductOptions = [],
  serviceProductOptionsStatus = 'ready',
  onRetryServiceProductOptions,
  onlineStoreEnabled,
  mobile = false,
  desktopRail = false,
}: Props) {
  const serviceQuotesEnabled = useHasBusinessCapability('service_quotes');
  const mercadoPagoEnabled = useHasBusinessCapability('mp_oauth');
  const evalOptions = useMemo(
    () => serviceProductOptions.filter((p) => p.id && p.bookingMode !== 'quote_required'),
    [serviceProductOptions],
  );
  const paymentModeOptions: readonly { value: ProductDraft['bookingPaymentMode']; label: string }[] =
    mercadoPagoEnabled ? PAYMENT_MODE_SEGMENTS : PAYMENT_MODE_SEGMENTS.slice(0, 2);
  const quoteSettingsEnabled = serviceQuotesEnabled && onlineStoreEnabled;
  const savedQuoteNeedsCapability = !quoteSettingsEnabled && draft.bookingMode === 'quote_required';
  const savedPaymentNeedsMercadoPago =
    !mercadoPagoEnabled &&
    (draft.bookingPaymentMode === 'deposit' || draft.bookingPaymentMode === 'full');

  if (draft.itemType !== 'service') return null;

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Reserva y cobro</Text> : null}

      {quoteSettingsEnabled ? (
        <>
          <Text style={styles.ticketFormLabel}>Modo de reserva</Text>
          <CatalogProductFormSegmentedRow
            options={BOOKING_MODE_SEGMENTS}
            value={draft.bookingMode}
            onChange={(id) => onBookingModeChange?.(id)}
            testIDPrefix="catalog.form.booking-mode"
          />
          <Text style={styles.ticketFormHint}>
            Cotización: el cliente pide presupuesto antes de agendar.
          </Text>
        </>
      ) : savedQuoteNeedsCapability ? (
        <Text style={styles.ticketFormHint}>
          {serviceQuotesEnabled
            ? 'Esta cotización sigue guardada. Activá Tienda online en Funciones del negocio para editarla.'
            : 'Esta cotización sigue guardada. Activá «Hacés presupuestos» en Funciones del negocio para editarla.'}
        </Text>
      ) : null}

      {quoteSettingsEnabled && draft.bookingMode === 'quote_required' ? (
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>Servicio de evaluación (opcional)</Text>
          <Text style={styles.ticketFormHint}>
            Turno corto para ver al cliente. Dejá vacío si no aplica.
          </Text>
          {serviceProductOptionsStatus === 'error' ? (
            <View>
              <Text style={styles.ticketFormHint}>
                No pudimos cargar los servicios de evaluación.
              </Text>
              {onRetryServiceProductOptions ? (
                <TouchableOpacity
                  style={styles.createMasterLink}
                  onPress={onRetryServiceProductOptions}
                  accessibilityRole="button"
                  accessibilityLabel="Reintentar carga de servicios"
                >
                  <Text style={styles.createMasterLinkText}>Reintentar</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : serviceProductOptionsStatus === 'loading' ||
            serviceProductOptionsStatus === 'idle' ? (
            <Text style={styles.ticketFormHint}>Buscando servicios de evaluación…</Text>
          ) : evalOptions.length === 0 ? (
            <Text style={styles.ticketFormHint}>
              Creá antes un servicio corto (ej. Evaluación) con reserva directa.
            </Text>
          ) : (
            evalOptions.map((opt) => {
              const selected = draft.evaluationProductId === opt.id;
              return (
                <View
                  key={opt.id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 8,
                  }}
                >
                  <Text style={styles.ticketFormLabel}>{opt.name}</Text>
                  <Switch
                    value={selected}
                    onValueChange={(on) => onEvaluationProductIdChange?.(on ? opt.id : '')}
                    trackColor={{ false: ticketColors.ink3, true: ticketColors.ok }}
                  />
                </View>
              );
            })
          )}
        </View>
      ) : null}

      <View style={mobile ? styles.fieldGridMobile : styles.fieldGrid}>
        <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
          <Text style={styles.ticketFormLabel}>Duración (minutos)</Text>
          <TextInput
            style={styles.ticketFormInput}
            value={draft.durationMinutes}
            onChangeText={(value) => onDurationMinutesChange?.(value)}
            placeholder="Ej. 45"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="number-pad"
          />
          <Text style={styles.ticketFormHint}>Cuánto dura el turno en la agenda (ej. 45).</Text>
        </View>
        {onlineStoreEnabled ? (
          <>
            <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
              <Text style={styles.ticketFormLabel}>Minutos libres antes</Text>
              <TextInput
                style={styles.ticketFormInput}
                value={draft.bufferBeforeMinutes}
                onChangeText={(value) => onBufferBeforeMinutesChange?.(value)}
                placeholder="0"
                placeholderTextColor={ticketColors.ink3}
                keyboardType="number-pad"
              />
              <Text style={styles.ticketFormHint}>Tiempo libre antes de empezar. 0 = sin hueco.</Text>
            </View>
            <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
              <Text style={styles.ticketFormLabel}>Minutos libres después</Text>
              <TextInput
                style={styles.ticketFormInput}
                value={draft.bufferAfterMinutes}
                onChangeText={(value) => onBufferAfterMinutesChange?.(value)}
                placeholder="0"
                placeholderTextColor={ticketColors.ink3}
                keyboardType="number-pad"
              />
              <Text style={styles.ticketFormHint}>Tiempo libre al terminar. 0 = sin hueco.</Text>
            </View>
          </>
        ) : null}
      </View>
      {onlineStoreEnabled ? (
        <Text style={styles.ticketFormHint}>
          Solo en reservas por internet: ese rato no se ofrece a otro. El servicio dura lo mismo.
        </Text>
      ) : null}

      {onlineStoreEnabled ? (
        <>
          <Text style={[styles.ticketFormLabel, { marginTop: 12 }]}>Cobro al reservar</Text>
          {savedPaymentNeedsMercadoPago ? (
            <Text style={styles.ticketFormHint}>
              Este cobro está guardado. Activá Mercado Pago en Funciones del negocio para editarlo.
            </Text>
          ) : (
            <>
              <CatalogProductFormSegmentedRow
                options={paymentModeOptions}
                value={draft.bookingPaymentMode}
                onChange={(id) => onBookingPaymentModeChange?.(id)}
                testIDPrefix="catalog.form.booking-payment-mode"
              />
              <Text style={styles.ticketFormHint}>
                {mercadoPagoEnabled
                  ? 'Como la tienda: la seña de tu local. Seña o total, solo para este servicio.'
                  : 'Activá Mercado Pago en Funciones del negocio para ofrecer seña o pago total.'}
              </Text>
            </>
          )}
        </>
      ) : null}

      {onlineStoreEnabled && mercadoPagoEnabled && draft.bookingPaymentMode === 'deposit' ? (
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>Seña en pesos (opcional)</Text>
          <TextInput
            style={styles.ticketFormInput}
            value={draft.bookingDepositAmount}
            onChangeText={(v) => onBookingDepositAmountChange?.(v)}
            placeholder="Dejá vacío si usás la seña de la tienda"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="decimal-pad"
          />
          <Text style={[styles.ticketFormLabel, { marginTop: 8 }]}>Seña en % (opcional)</Text>
          <TextInput
            style={styles.ticketFormInput}
            value={draft.bookingDepositPercent}
            onChangeText={(v) => onBookingDepositPercentChange?.(v)}
            placeholder="Ej. 30"
            placeholderTextColor={ticketColors.ink3}
            keyboardType="decimal-pad"
          />
        </View>
      ) : null}
    </View>
  );
}
